// HOME'S OPENED ONGOING LIST (busy-my-web#630, Pouria 2026-10-14).
//
//   "when we are in home i have like • 13 ongoings need your attention when we click on it the
//    follow up should be disipear and we need tio see some briefing and those items after click on
//    the need attention shuild be in middle in main page listed we have lisiting component as well"
//
// An Ongoing count row on Home ("13 ongoings need your attention", "4 ongoings overdue", "5 ongoings
// waiting on someone") used to UNFOLD inside the Follow-ups strip — the "All follow-ups" back row
// and three stories at most (`ongoingGroupRows`, retired with this issue). It now does what the
// "Emails need attention" row does: the strip steps aside, the box heads itself with a Briefing
// about the list, and EVERY counted Ongoing is listed in the middle of Home in the listing row's
// own look (lib/entityListingRow.js's ROW geometry, the 46px stamp).
//
// The list is opened in the briefing store (`briefing/openOngoingList`) because three components
// with no relationship to each other read it: FollowUps.vue hides while it is up, views/Home.vue
// draws the rows, and the SmartBox heads the box with its card and puts Close in the gutter.
//
// Pure — no store, no router, nothing mounted. The count row hands over `{ id, title }` per story;
// the ongoing list (`ongoing/items`) is read here for everything a listing row says beyond the name
// (where the story came from, where it stands on the ladder, when it last moved), so a story the
// count knew about but the list has not loaded yet still gets a row — named, opening its page,
// and on the rung the count was made on.

import { briefing, countLabel, countText } from '@/lib/briefings/rows'
import { listingDateLabel } from '@/lib/listingDate'
import { listStatusMeta } from '@/lib/dueStatus'
import { originMeta } from '@/lib/ongoingOrigin'
import { ongoingRoute } from '@/lib/storyAutomation'
import { statusRung, uniqueById } from '@/lib/homeFollowUps'

/** The briefing kind the store's `active` answers while the list is open. */
export const ONGOING_LIST_KIND = 'ongoing-list'

/**
 * The opened list, as the store keeps it — or null when the group cannot be one.
 *
 * Every story at most once (busy-my-web#571's rule), stories with no id dropped (they have no page
 * to open), and nothing left means nothing opened: a list of zero is not a place to stand.
 */
export function ongoingListOf(group) {
  if (!group || typeof group !== 'object') return null
  const ongoings = uniqueById(group.ongoings)
    .filter(o => o && String(o.id ?? '').trim())
    .map(o => ({ id: String(o.id).trim(), title: String(o.title || '').trim() }))
  if (!ongoings.length) return null
  return {
    section: String(group.section || ''),
    label: String(group.label || '').trim(),
    status: String(group.status || ''),
    ongoings,
  }
}

/** The ongoing list indexed by id, compared case-aside (a waits id arrives lower-cased). */
function byId(items) {
  const map = new Map()
  for (const o of Array.isArray(items) ? items : []) {
    if (o && o.id) map.set(String(o.id).toLowerCase(), o)
  }
  return map
}

/**
 * The rung a listed story is on: its own, from the list, when the list holds it — else the rung
 * the count was made on (a waited-on story the list has not loaded is still a waited-on story).
 */
function rungOf(record, list) {
  return statusRung(record?.status) ? String(record.status).trim() : String(list?.status || '')
}

/**
 * One listing row per counted story, in the order the count gave them.
 *
 *   { id, title, icon, iconColor, status, statusLabel, statusColor, dateLabel, route }
 *
 * The glyph and its colour are the sidebar's for that story (lib/ongoingOrigin.js `originMeta`), and
 * the status word is the sidebar's too (lib/dueStatus.js `listStatusMeta` — "Needs attention" in red,
 * a quiet story's purple "active"), so a story reads the same in the middle as it does on the left.
 * The stamp is when it last MOVED — a note's stamp ("11:35" today, "8 Jun", "8 Jun 25"), read off
 * `updateDate` and falling back to when it was made.
 */
export function ongoingListingRows(list, items = [], now = new Date()) {
  const known = byId(items)
  return (list?.ongoings || [])
    .map((o) => {
      const record = known.get(String(o?.id || '').toLowerCase()) || null
      const origin = originMeta(record?.origin)
      const status = rungOf(record, list)
      const meta = listStatusMeta(status)
      return {
        id: String(o?.id || ''),
        title: String(record?.title || o?.title || '').trim() || 'Ongoing',
        icon: origin.icon,
        iconColor: origin.color,
        status,
        statusLabel: meta.label,
        statusColor: meta.color,
        dateLabel: listingDateLabel({ updateDate: record?.updateDate || record?.createdDate || null }, 'notes', now),
        route: ongoingRoute(o?.id),
      }
    })
    .filter(r => r.route)
}

/**
 * The listing's section header words: the count row's own label without its number — "Ongoings
 * need your attention" — because ListingSectionHeader draws the count beside them as its subscript,
 * and "13 ongoings need your attention 13" would say it twice. First letter up, as a header is.
 */
export function ongoingListHeading(list) {
  const words = String(list?.label || '').trim().replace(/^\d[\d,]*\s+/, '')
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : 'Ongoings'
}

/** The header's ink: the listings' own red for a list that is about lateness, else the row ink. */
export function ongoingListVariant(list) {
  return ['overdue', 'needsattention'].includes(statusRung(list?.status)) ? 'overdue' : 'default'
}

// The words a rung takes in the Briefing's breakdown, singular first. Not the sidebar's chip words:
// those label ONE story ("Needs attention"), and a count of nine is "9 Need attention".
const RUNG_WORDS = {
  needsattention: ['Needs attention', 'Need attention'],
  overdue: ['Overdue', 'Overdue'],
  waiting: ['Waiting on someone', 'Waiting on someone'],
  duesoon: ['Due soon', 'Due soon'],
}
const RUNG_ICON = { needsattention: 'notification-bing', overdue: 'notification-off', waiting: 'clock', duesoon: 'notification-bing' }

/**
 * How the listed stories split across the ladder, rung by rung in the order they first appear —
 * `[{ key, status, count, text, icon }]`. A story on no named rung counts as "Active".
 */
export function ongoingListBreakdown(rows) {
  const order = []
  const counts = {}
  for (const r of Array.isArray(rows) ? rows : []) {
    // One "Active" row for EVERY rung with no words of its own (upcoming, happening-now, unranked):
    // the waiting row's stories carry their own rungs, and three rows each reading "1 Active" say
    // nothing the middle's rows do not already say better ("Upcoming", "Happening now").
    const rung = statusRung(r?.status)
    const key = RUNG_WORDS[rung] ? rung : 'active'
    if (!(key in counts)) { counts[key] = 0; order.push(key) }
    counts[key] += 1
  }
  return order.map((key) => {
    const n = counts[key]
    const [one, many] = RUNG_WORDS[key] || ['Active', 'Active']
    return { key: `rung-${key}`, status: key, count: n, text: `${countText(n)} ${n === 1 ? one : many}`, icon: RUNG_ICON[key] || 'story' }
  })
}

/**
 * The Briefing for the opened list: "Home › ⟨Briefing · 13 ongoings need your attention⟩" — the
 * count row's own words as the subject, the way an open sender group heads the email card
 * ("Briefing · LinkedIn") — over "13 Ongoings" and, when the list mixes rungs (the waiting row's
 * stories carry their own), one row per rung: "9 Need attention", "4 Overdue". Capital after the
 * number, as every Briefing row has it (busy-my-web#609).
 *
 * Plain facts, no filters: the list in the middle IS the subject, and there is nothing under it to
 * narrow. Null when nothing is open.
 */
export function ongoingListBriefing(list, items = [], now = new Date()) {
  if (!list || !Array.isArray(list.ongoings) || !list.ongoings.length) return null
  const rows = ongoingListingRows(list, items, now)
  const split = ongoingListBreakdown(rows)
  return briefing({
    kind: ONGOING_LIST_KIND,
    title: list.label ? `Briefing · ${list.label}` : 'Briefing',
    titleIcon: 'story',
    // Near-black, like the email card it mirrors (busy-my-web#316's header colour).
    titleColor: '#111827',
    rows: [
      { key: 'ongoings', icon: 'story', text: countLabel(rows.length, 'Ongoing') },
      ...(split.length > 1 ? split.map(s => ({ key: s.key, icon: s.icon, text: s.text })) : []),
    ],
  })
}
